import type { Lead, Project, SiteContent } from '../types'

const projectId = 'blackrock-developers-wnc'
const apiKey = 'AIzaSyBKFrR3g87b_K-AmY6K73H5VscLtp9qwmg'
const documentsBase = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents`

type FirestoreValue = {
  stringValue?: string
  booleanValue?: boolean
  integerValue?: string
  doubleValue?: number
  timestampValue?: string
  nullValue?: null
  mapValue?: { fields?: Record<string, FirestoreValue> }
  arrayValue?: { values?: FirestoreValue[] }
}

type FirestoreDocument = {
  name: string
  fields?: Record<string, FirestoreValue>
}

function decodeValue(value: FirestoreValue): unknown {
  if ('stringValue' in value) return value.stringValue
  if ('booleanValue' in value) return value.booleanValue
  if ('integerValue' in value) return Number(value.integerValue)
  if ('doubleValue' in value) return value.doubleValue
  if ('timestampValue' in value) return value.timestampValue
  if ('nullValue' in value) return null
  if ('arrayValue' in value) return (value.arrayValue?.values || []).map(decodeValue)
  if ('mapValue' in value) return decodeFields(value.mapValue?.fields || {})
  return undefined
}

function decodeFields(fields: Record<string, FirestoreValue>) {
  return Object.fromEntries(Object.entries(fields).map(([key, value]) => [key, decodeValue(value)]))
}

function documentId(document: FirestoreDocument) {
  return document.name.split('/').pop() || ''
}

export async function fetchPublicSiteContent(signal?: AbortSignal) {
  const contentResponse = await fetch(`${documentsBase}/site/content?key=${apiKey}`, { signal })
  if (contentResponse.ok) {
    const document = await contentResponse.json() as FirestoreDocument
    return decodeFields(document.fields || {}) as Partial<SiteContent>
  }
  return undefined
}

export async function fetchPublicProjects(signal?: AbortSignal) {
  const projectsResponse = await fetch(`https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents:runQuery?key=${apiKey}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      structuredQuery: {
        from: [{ collectionId: 'projects' }],
        orderBy: [{ field: { fieldPath: 'order' }, direction: 'ASCENDING' }],
      },
    }),
    signal,
  })

  if (projectsResponse.ok) {
    const rows = await projectsResponse.json() as { document?: FirestoreDocument }[]
    const projects = rows
      .filter((row): row is { document: FirestoreDocument } => Boolean(row.document))
      .map(({ document }) => ({ id: documentId(document), ...decodeFields(document.fields || {}) }) as Project)
    if (projects.length) return projects
  }
  return undefined
}

export async function fetchPublicContent(): Promise<{ content?: Partial<SiteContent>; projects?: Project[] }> {
  const [content, projects] = await Promise.all([
    fetchPublicSiteContent(),
    fetchPublicProjects(),
  ])
  return { content, projects }
}

export async function submitInquiry(lead: Omit<Lead, 'id' | 'createdAt'>) {
  const fields: Record<string, FirestoreValue> = {
    name: { stringValue: lead.name },
    email: { stringValue: lead.email },
    phone: { stringValue: lead.phone },
    projectType: { stringValue: lead.projectType },
    message: { stringValue: lead.message },
    status: { stringValue: 'new' },
    createdAt: { timestampValue: new Date().toISOString() },
    consentAt: { timestampValue: new Date().toISOString() },
    consentVersion: { stringValue: '2026-07-22' },
  }
  const response = await fetch(`${documentsBase}/leads?documentId=${crypto.randomUUID()}&key=${apiKey}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fields }),
  })
  if (!response.ok) throw new Error('Inquiry could not be sent')
}
